import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import type { Props as ReactApexChartProps } from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';

type ApexRecord = Record<string, unknown>;

interface ChartBaseProps {
  series: NonNullable<ApexOptions['series']>;
  options?: ApexOptions;
  type?: ReactApexChartProps['type'];
  height?: number | string;
  width?: number | string;
  id?: string;
}

// Helper to read CSS variables with fallback
function getCssVar(name: string, fallback = '') {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return fallback;
  }
  const body = document.body;
  const doc = document.documentElement;
  const v1 = getComputedStyle(body).getPropertyValue(name)?.trim();
  if (v1) {
    return v1;
  }
  const v2 = getComputedStyle(doc).getPropertyValue(name)?.trim();
  if (v2) {
    return v2;
  }
  return fallback;
}

// Helper to build default options outside the component (reduces function complexity)
function buildDefaultOptions(isDark: boolean, textColor: string, id?: string) {
  let themeMode: 'dark' | 'light' = 'light';
  if (isDark) { themeMode = 'dark'; }

  return {
    chart: {
      id: id ?? 'chart',
      foreColor: textColor,
      toolbar: { show: true }
    },
    theme: {
      mode: themeMode
    },
    xaxis: {
      labels: {
        style: { fontSize: '11px', colors: [textColor] }
      }
    },
    yaxis: {
      labels: {
        style: { colors: [textColor] }
      }
    },
    legend: {
      labels: { colors: textColor }
    },
    tooltip: {
      theme: themeMode,
    },
    noData: { text: 'No Data' }
  };
}

function mergeOptions(defaultOpts: ApexOptions, overrides: ApexOptions): ApexOptions {
  const merged: ApexRecord = { ...(defaultOpts as ApexRecord), ...(overrides as ApexRecord) };
  const nestedKeys = ['chart', 'xaxis', 'yaxis', 'tooltip', 'legend'];

  nestedKeys.forEach(key => {
    const base = ((defaultOpts as ApexRecord)[key] ?? {}) as ApexRecord;
    const override = ((overrides as ApexRecord)[key] ?? {}) as ApexRecord;
    merged[key] = { ...base, ...override };
  });

  return merged as ApexOptions;
}

const ChartBase: React.FC<ChartBaseProps> = ({ series, options = {}, type = 'bar', height = 350, width = '100%', id }) => {
  const isDark = typeof document !== 'undefined' && document.body.classList.contains('dark-theme');

  let textColorFallback = '#222222';
  if (isDark) { textColorFallback = '#ffffff'; }
  const textColor = getCssVar('--text-color', textColorFallback);

  // Common default options applied to all charts
  const defaultOptions = useMemo(() => buildDefaultOptions(isDark, textColor, id), [isDark, textColor, id]);

  // Merge user-provided options with defaults (shallow nested merge)
  const mergedOptions = useMemo(() => mergeOptions(defaultOptions, options), [defaultOptions, options]);

  return (
    <Chart
      options={mergedOptions}
      series={series}
      type={type}
      height={height}
      width={width}
    />
  );
};

export default ChartBase;